import React, { useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const Statystyki = ({ wyniki }) => {
  const { statystyki, arkusze } = wyniki;

  // Dane do wykresu kołowego
  const pieData = [
    { name: "Wykorzystany materiał", value: statystyki.procent_wykorzystania },
    { name: "Odpady", value: 100 - statystyki.procent_wykorzystania },
  ];

  // Dane do wykresu słupkowego
  const barData = arkusze.map((arkusz, index) => ({
    name: `Arkusz ${index + 1}`,
    "Wykorzystanie (%)": arkusz.wykorzystanie,
    "Liczba formatów": arkusz.formatki.length,
  }));

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  const [rollWidthCm, setRollWidthCm] = useState(
    (arkusze && arkusze[0] && arkusze[0].szerokosc / 10) || 0,
  );

  const totalAreaMm2 =
    statystyki?.laczna_powierzchnia_formatow ||
    arkusze.reduce(
      (sum, a) =>
        sum +
        (a.formatki
          ? a.formatki.reduce((s, f) => s + f.szerokosc * f.wysokosc, 0)
          : 0),
      0,
    );

  const rollWidthMm = rollWidthCm * 10;
  const totalMb = rollWidthMm > 0 ? totalAreaMm2 / rollWidthMm / 1000 : 0;

  const arkuszeMb = arkusze.map((a) => {
    const area = a.formatki
      ? a.formatki.reduce((s, f) => s + f.szerokosc * f.wysokosc, 0)
      : 0;
    return {
      id: a.id,
      szerokosc: a.szerokosc,
      wysokosc: a.wysokosc,
      mb: rollWidthMm > 0 ? area / rollWidthMm / 1000 : 0,
      area,
    };
  });

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Wykres kołowy */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Wykorzystanie materiału
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(1)}%`
                    }
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Paski postępu */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Statystyki szczegółowe
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                Całkowite wykorzystanie: {statystyki.procent_wykorzystania}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={statystyki.procent_wykorzystania}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#e3f2fd" }}>
                  <Typography variant="h4" color="primary">
                    {statystyki.liczba_arkuszy}
                  </Typography>
                  <Typography variant="body2">Arkuzy</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#f3e5f5" }}>
                  <Typography variant="h4" color="secondary">
                    {statystyki.liczba_formatow}
                  </Typography>
                  <Typography variant="body2">Formatów</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Potrzebne metry bieżące (mb) — przy założeniu szerokości rolki:
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  label="Szer. rolki (cm)"
                  type="number"
                  value={rollWidthCm}
                  onChange={(e) =>
                    setRollWidthCm(parseFloat(e.target.value) || 0)
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">cm</InputAdornment>
                    ),
                  }}
                  sx={{ width: 180 }}
                />

                <Paper variant="outlined" sx={{ p: 1, minWidth: 160 }}>
                  <Typography variant="h6">{totalMb.toFixed(2)} mb</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Łącznie
                  </Typography>
                </Paper>

                <Box>
                  {arkuszeMb.map((a) => (
                    <Typography key={a.id} variant="body2">
                      Arkusz {a.id + 1}: {a.mb.toFixed(2)} mb
                    </Typography>
                  ))}
                </Box>
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                Wzór: mb = powierzchnia formatów (m²) / szer. rolki (m). Zmień
                szer. rolki aby przeliczyć.
              </Typography>

              {statystyki.liczba_formatow_nieumieszczonych > 0 && (
                <Typography color="error" sx={{ mt: 1 }}>
                  ⚠️ Uwaga: {statystyki.liczba_formatow_nieumieszczonych}{" "}
                  format(ek) nie zmieściło się — użyto{" "}
                  {statystyki.liczba_arkuszy} arkuszy.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Wykres słupkowy */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Wykorzystanie poszczególnych arkuszy
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Wykorzystanie (%)" fill="#8884d8" />
                  <Bar dataKey="Liczba formatów" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Tabela szczegółów */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Szczegóły rozmieszczenia
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Arkusz</TableCell>
                    <TableCell align="right">Wymiary (cm)</TableCell>
                    <TableCell align="right">Wykorzystanie</TableCell>
                    <TableCell align="right">Liczba formatów</TableCell>
                    <TableCell align="right">
                      Powierzchnia formatów (m²)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {arkusze.map((arkusz, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        Arkusz {index + 1}
                      </TableCell>
                      <TableCell align="right">
                        {arkusz.szerokosc / 10} × {arkusz.wysokosc / 10}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Box sx={{ width: 50, mr: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={arkusz.wykorzystanie}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          {arkusz.wykorzystanie.toFixed(1)}%
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {arkusz.formatki.length}
                      </TableCell>
                      <TableCell align="right">
                        {(
                          arkusz.formatki.reduce(
                            (sum, f) => sum + f.szerokosc * f.wysokosc,
                            0,
                          ) / 1000000
                        ).toFixed(3)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Statystyki;
